import { useQuery, type QueryClient } from '@tanstack/react-query';
import { fetchToolListingStatus, NotConnectedToFigmaError } from '@/api/workflowCommunityPublishing.api';
import { FF_FIGMA_ORG_SCOPED_PUBLISHING } from '@/consts/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useLinkedFigmaAccount } from '@/hooks/useLinkedFigmaAccount';

export const toolListingStatusQueryKey = (toolId: string | undefined) => ['tool-listing-status', toolId] as const;

/**
 * Requires edit access to the tool's workflow. The rollout flag gates this management read;
 * an org admin disabling new publishes does not prevent reading or unpublishing its listing.
 */
export const useToolListingStatus = (toolId: string | undefined) => {
  const { value: orgScopedPublishingFlag } = useFeatureFlag(FF_FIGMA_ORG_SCOPED_PUBLISHING);
  const isOrgScopedPublishingEnabled = orgScopedPublishingFlag === true;
  const { data: connection } = useLinkedFigmaAccount();
  const figmaUserId = connection?.connected ? connection.figmaUserId : null;
  const query = useQuery({
    queryKey: [...toolListingStatusQueryKey(toolId), figmaUserId],
    queryFn: () => fetchToolListingStatus(toolId!),
    enabled: isOrgScopedPublishingEnabled && Boolean(toolId) && connection?.connected === true,
    retry: (failureCount, error) => !(error instanceof NotConnectedToFigmaError) && failureCount < 1,
  });
  // Disabled queries retain cached data; turning the rollout off must restore Community metadata.
  return { ...query, data: isOrgScopedPublishingEnabled ? query.data : undefined, isOrgScopedPublishingEnabled };
};

export const invalidateToolListingStatus = (queryClient: QueryClient, toolId: string) =>
  queryClient.invalidateQueries({ queryKey: toolListingStatusQueryKey(toolId) });
